"use client";
import * as React from "react";
import {
  DayPicker,
  DayPickerProps,
  SelectMultipleEventHandler,
  SelectRangeEventHandler,
  SelectSingleEventHandler,
} from "react-day-picker";
import { buttonVariants } from "@/components/atoms/Button/button-variants";
import ChevronLeftIcon from "@/icons/__generated__/ChevronLeft/ChevronLeft20";
import ChevronRightIcon from "@/icons/__generated__/ChevronRight/ChevronRight20";
import { cn } from "@/lib/utils";
import { getCalendarLocale } from "@/lib/utils/calendar-utils";
import { CalendarLoadingOverlay } from "./CalendarLoadingOverlay";

export type CalendarProps = Omit<DayPickerProps, "locale"> & {
  showLoading?: boolean;
  fullWidth?: boolean;
  locale?: string;
  // defining onSelect here due to weirdness with Omit
  onSelect?:
    | SelectSingleEventHandler
    | SelectMultipleEventHandler
    | SelectRangeEventHandler;
};

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = false,
  showLoading = false,
  fullWidth = false,
  footer,
  components,
  modifiersClassNames,
  locale = "en-US",
  mode,
  ...dayPickerProps
}: CalendarProps) => {
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    // need an extra render initially to show the footer and loading overlay properly
    // (they are rendered with createPortal and need the calendar container to be mounted)
    setIsMounted(true);
  }, []);

  return (
    <>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn(
          "!tw-m-0 tw-rounded-4 tw-bg-base-white calendar-container tw-shadow-xs tw-relative",
          {
            "tw-w-full": fullWidth,
          },
          className,
        )}
        classNames={{
          months: "tw-flex tw-flex-col sm:tw-flex-row tw-flex-wrap",
          month: cn(
            "tw-space-y-16 tw-w-full tw-p-16 tw-flex-1 tw-relative [&.rdp-caption_end]:!tw-hidden",
            {
              "tw-min-w-[280px] tw-max-w-[352px]": !fullWidth,
              "tw-pb-0": !!footer,
            },
          ),
          caption: "tw-flex tw-justify-between tw-relative tw-items-center",
          caption_label: "tw-w-full tw-text-center tw-body-2-m",
          nav: "tw-flex tw-items-center tw-justify-between tw-gap-8 tw-absolute tw-inset-0 calendar-nav [&:has(.next-month-icon):not(:has(.prev-month-icon))]:tw-justify-end",
          nav_button: cn(
            buttonVariants({ variant: "ghost" }),
            "tw-size-28 tw-bg-transparent tw-p-0",
          ),
          table: cn("tw-w-full tw-border-collapse tw-relative tw-space-y-4"),
          head_row: "tw-flex",
          head_cell: "tw-w-full tw-caption-2 tw-text-gray-600 tw-text-center",
          row: "tw-flex tw-w-full tw-mt-4",
          cell: "tw-h-32 tw-w-full tw-text-center tw-p-0 tw-relative focus-within:tw-relative focus-within:tw-z-20 [&:has([aria-selected].day-range-end)]:tw-bg-gray-300 [&:has([aria-selected].day-range-start)]:tw-bg-gray-300 [&:has([aria-selected].day-range-middle)]:tw-bg-gray-300 [&:has([aria-selected].day-range-start.day-range-end)]:tw-bg-transparent [&:has([aria-selected].day-range-start)]:tw-rounded-l-4 [&:has([aria-selected].day-range-end)]:tw-rounded-r-4 first:[&:has([aria-selected])]:tw-rounded-l-4 last:[&:has([aria-selected])]:tw-rounded-r-4",
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "tw-size-32 tw-p-0 tw-body-1 aria-selected:tw-opacity-100 [&.day-outside]:tw-text-gray-600 [&.tw-day-today]:tw-text-purple-800 [&.day-selected]:!tw-text-base-white [&.day-selected]:tw-bg-gray-900 tw-rounded-4 disabled:!tw-text-gray-500 disabled:tw-pointer-events-none disabled:tw-line-through disabled:[&.day-selected]:!tw-text-base-white [&.day-range-middle.day-selected]:!tw-bg-transparent [&.day-range-middle.day-selected]:!tw-text-gray-900 [&.day-range-middle.tw-day-today]:!tw-text-purple-800",
          ),
          day_range_start: "day-range-start",
          day_range_end: "day-range-end",
          day_selected: "day-selected",
          day_today: "tw-day-today",
          day_outside: "day-outside",
          day_range_middle: "day-range-middle",
          day_hidden: "tw-invisible",
          tbody: "tw-block tw-relative calendar-body",
          ...classNames,
        }}
        modifiersClassNames={{
          fullyBooked: "tw-pointer-events-none",
          ...(modifiersClassNames || {}),
        }}
        mode={mode as any} // using Omit above causing weirdness :(
        components={{
          IconLeft: () => <ChevronLeftIcon className="prev-month-icon" />,
          IconRight: () => <ChevronRightIcon className="next-month-icon" />,
          ...(components || {}),
        }}
        locale={getCalendarLocale(locale)}
        {...dayPickerProps}
      />

      {isMounted && (
        <>
          {showLoading && <CalendarLoadingOverlay />}
          {footer}
        </>
      )}
    </>
  );
};
Calendar.displayName = "Calendar";
